import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Topbar from '../components/Topbar';
import { getAirportsSelection, getConcoursesSelection, createPOI, getPOIs, getSinglePOI, updatePOI, deletePOI, addPOIImage, deletePOIImage, deleteMainImage, updatePOIStatus, getPendingImages, approveImage, rejectImage, savePOIDynamicFields, getPOIDynamicFields, getPOIReviews, getPOIReports, approvePOIReport, rejectPOIReport } from '../services/poiServices';
import { getFloors } from '../services/airportService';
import { getCategories } from '../services/categoryService';
import { getDynamicFields } from '../services/dynamicFieldService';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import { useRefresh } from '../utils/RefreshContext';

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];



const POIManagement = () => {
  const { refreshKey } = useRefresh();
  const [searchParams] = useSearchParams();
  const imgBaseUrl = process.env.REACT_APP_IMG_BASE_URL;
  const [viewMode, setViewMode] = useState('list'); // 'list', 'add', 'edit', 'view', 'reports'
  const [poiList, setPoiList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, id: null });
  const [imageDeleteModal, setImageDeleteModal] = useState({ show: false, index: null });
  const [mainImageDeleteModal, setMainImageDeleteModal] = useState({ show: false });
  const [selectedImage, setSelectedImage] = useState(null);

  // Selection data
  const [airports, setAirports] = useState([]);
  const [concourses, setConcourses] = useState([]);
  const [floors, setFloors] = useState([]);
  const [loadingConcourses, setLoadingConcourses] = useState(false);
  const [loadingFloors, setLoadingFloors] = useState(false);
   const [categories, setCategories] = useState([]);
    const [viewData, setViewData] = useState(null);
    const [pendingImages, setPendingImages] = useState([]);
    const [selectedPendingImages, setSelectedPendingImages] = useState([]);
    const [rejectModal, setRejectModal] = useState({ show: false, imageId: null, reason: '' });
    const [reportActionModal, setReportActionModal] = useState({ show: false, reportId: null, action: null, reason: '' });
    const [availableDynamicFields, setAvailableDynamicFields] = useState([]);
    const [poiDynamicFields, setPoiDynamicFields] = useState([]);
    const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    search: '',
    airport_id: '',
    category_id: '',
    status: ''
  });
  const [meta, setMeta] = useState({ total: 0, totalPages: 1, currentPage: 1, limit: 10 });
  const [selectedIds, setSelectedIds] = useState([]);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    airport_id: '',
    concourse_id: '',
    category_id: '',
    floor_id: '',
    floor: '',
    lat: '',
    lng: '',
    description: '',
    tags: '', // Comma separated string for backend, but we manage as array in UI
    phone_number: '',
    website_url: '',
    internal_notes: '',
    status: 1,
    images: [], // Binary files
    main_image: null // Main image file
  });

  const [imagePreviews, setImagePreviews] = useState([]);
  const [mainImagePreview, setMainImagePreview] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [tagsArray, setTagsArray] = useState([]);

  // Category Search States
  const [catSearch, setCatSearch] = useState('');
  const [isCatDropdownOpen, setIsCatDropdownOpen] = useState(false);
  const [isCatSearching, setIsCatSearching] = useState(false);
  const catDropdownRef = useRef(null);
  // Airport search state
  const [airportSearch, setAirportSearch] = useState('');
  const [isAirportDropdownOpen, setIsAirportDropdownOpen] = useState(false);
  const [isAirportSearching, setIsAirportSearching] = useState(false);
  const airportDropdownRef = useRef(null);

  const [operatingHours, setOperatingHours] = useState(
    days.map(day => ({
      day,
      open_time: '06:00',
      close_time: '22:00',
      is_closed: day === 'Sunday'
    }))
  );

  const [accessibility, setAccessibility] = useState([]);
  const [poiReviews, setPoiReviews] = useState([]);
  const [poiRatings, setPoiRatings] = useState(null);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [poiReports, setPoiReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);

  const fetchPOIsData = useCallback(async (params = filters) => {
    setIsLoading(true);
    try {
      const res = await getPOIs(params);
      if (res && res.result) {
        setPoiList(res.data);
        setMeta(res.meta || { total: res.data.length, totalPages: 1, currentPage: 1, limit: filters.limit });
      }
    } catch (error) {
      console.log('An error occurred : ', error);
      // toast.error("Failed to load POIs");
    } finally {
      setIsLoading(false);
    }
  }, []); // Decoupled from filters

  // Fetch initial non-POI data once
  useEffect(() => {
    const loadSelectionData = async () => {
      try {
        const [airportsRes, categoriesRes, dynamicFieldsRes] = await Promise.all([
          getAirportsSelection(),
          getCategories({ limit: 100 }), // Load more categories initially
          getDynamicFields({ limit: 100 })
        ]);
        if (airportsRes && airportsRes.result) setAirports(airportsRes.data);
        if (categoriesRes && categoriesRes.result) setCategories(categoriesRes.data);

        // Handle dynamic fields - check for data at different levels
        if (dynamicFieldsRes) {
          const fieldsData = dynamicFieldsRes.data || dynamicFieldsRes.result || [];
          console.log('Dynamic fields response:', dynamicFieldsRes);
          console.log('Dynamic fields data:', fieldsData);
          setAvailableDynamicFields(Array.isArray(fieldsData) ? fieldsData : []);
        } else {
          console.log('No dynamic fields response');
          setAvailableDynamicFields([]);
        }
      } catch (error) {
        console.log('An error occurred : ', error);
        setAvailableDynamicFields([]);
        // toast.error("Failed to load selection data");
      }
    };
    loadSelectionData();
  }, []);

  const prevSearch = useRef(filters.search);

  // Unified Fetch Effect with Search Debounce
  useEffect(() => {
    const searchChanged = prevSearch.current !== filters.search;
    prevSearch.current = filters.search;

    const timer = setTimeout(() => {
      fetchPOIsData(filters);
    }, searchChanged ? 500 : 0);

    return () => clearTimeout(timer);
  }, [filters, fetchPOIsData, refreshKey]);

  // Fetch concourses and floors when airport changes
  useEffect(() => {
    if (formData.airport_id) {
      const loadAirportDependents = async () => {
        setLoadingConcourses(true);
        setLoadingFloors(true);
        try {
          const [concRes, floorRes] = await Promise.all([
            getConcoursesSelection(formData.airport_id),
            getFloors(formData.airport_id)
          ]);

          if (concRes && concRes.result) {
            setConcourses(concRes.data);
            setFormData(prev => ({
              ...prev,
              concourse_id: concRes.data.some(c => String(c.id) === String(prev.concourse_id) || String(c.value) === String(prev.concourse_id)) ? prev.concourse_id : ''
            }));
          } else {
            setConcourses([]);
          }

          if (floorRes && floorRes.result) {
            setFloors(floorRes.data);
            setFormData(prev => {
              const floorLabel = typeof prev.floor === 'string' ? prev.floor : '';
              const matchedFloor = floorRes.data.find(f =>
                String(f.id) === String(prev.floor_id) ||
                String(f.name || f.label || f.floor_name) === floorLabel
              );

              return {
                ...prev,
                floor_id: matchedFloor?.id || '',
                floor: matchedFloor ? prev.floor : ''
              };
            });
          } else {
            setFloors([]);
            setFormData(prev => ({ ...prev, floor_id: '', floor: '' }));
          }
        } catch (error) {
          console.error(error);
          setConcourses([]);
          setFloors([]);
        } finally {
          setLoadingConcourses(false);
          setLoadingFloors(false);
        }
      };
      loadAirportDependents();
    } else {
      setConcourses([]);
      setFloors([]);
      setFormData(prev => ({ ...prev, concourse_id: '', floor_id: '', floor: '' }));
    }
  }, [formData.airport_id]);

  // Debounced search for categories
  useEffect(() => {
    if (!isCatSearching) return;

    const timer = setTimeout(async () => {
      try {
        const res = await getCategories({ search: catSearch, limit: 100 });
        if (res && res.result) {
          setCategories(res.data);
        }
      } catch (error) {
        console.log('Error fetching categories:', error);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [catSearch, isCatSearching]);

  // Debounced search for airports (starts after 3 chars)
  useEffect(() => {
    if (!isAirportSearching) return;
    if (airportSearch.length < 3) return;
    const timer = setTimeout(async () => {
      try {
        const res = await getAirportsSelection({ search: airportSearch });
        if (res && res.result) {
          setAirports(res.data);
        }
      } catch (error) {
        console.log('Error fetching airports:', error);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [airportSearch, isAirportSearching]);

  // Handle outside click for category dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (catDropdownRef.current && !catDropdownRef.current.contains(event.target)) {
        setIsCatDropdownOpen(false);
      }
      if (airportDropdownRef.current && !airportDropdownRef.current.contains(event.target)) {
        setIsAirportDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Selection Handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === poiList.length && poiList.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(poiList.map(p => p.id));
    }
  };

  const toggleSelectItem = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    setSelectedIds([]);
  }, [poiList]);

  const handleBulkStatusUpdate = async (status) => {
    if (selectedIds.length === 0) return;

    setIsLoading(true);
    try {
      const payload = {
        ids: selectedIds,
        status: status
      };
      const res = await updatePOIStatus(payload);
      if (res && (res.status === 200 || res.status === 201)) {
        toast.success(`Updated ${selectedIds.length} POIs status successfully`);
        setSelectedIds([]);
        fetchPOIsData();
      }
    } catch (error) {
      console.error(error);
      // toast.error("Failed to update bulk status");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: (name === 'airport_id' || name === 'concourse_id' || name === 'floor_id' || name === 'category_id' || name === 'status') ? parseInt(value) || '' : value
    }));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!tagsArray.includes(tagInput.trim())) {
        const newTags = [...tagsArray, tagInput.trim()];
        setTagsArray(newTags);
        setFormData(prev => ({ ...prev, tags: newTags.join(', ') }));
      }
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    const newTags = tagsArray.filter(t => t !== tag);
    setTagsArray(newTags);
    setFormData(prev => ({ ...prev, tags: newTags.join(', ') }));
  };

  const handleImageChange = async (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    if (viewMode === 'edit') {
      const currentCount = imagePreviews.length;
      const totalRequested = currentCount + files.length;

      if (totalRequested > 5) {
        return toast.error(`Maximum 5 photos allowed. You already have ${currentCount} photos, and you're trying to add ${files.length} more.`);
      }

      // Store new files locally — uploaded via addPOIImage after the update is saved
      const validEditFiles = files.filter(file => {
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} is too large (>5MB)`);
          return false;
        }
        return true;
      });

      setFormData(prev => ({
        ...prev,
        images: [...(prev.images || []), ...validEditFiles]
      }));

      const newEditPreviews = validEditFiles.map(file => ({
        id: null,
        url: URL.createObjectURL(file),
        isExisting: false,
        caption: ''
      }));
      setImagePreviews(prev => [...prev, ...newEditPreviews]);
    } else {
      if ((formData.images.length + files.length) > 5) {
        return toast.error("Maximum 5 photos allowed");
      }

      const validFiles = files.filter(file => {
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} is too large (>5MB)`);
          return false;
        }
        return true;
      });

      setFormData(prev => ({
        ...prev,
        images: [...(prev.images || []), ...validFiles]
      }));

      const newPreviews = validFiles.map(file => ({
        id: null,
        url: URL.createObjectURL(file),
        isExisting: false,
        caption: ''
      }));
      setImagePreviews(prev => [...prev, ...newPreviews]);
    }
  };

  const handleCaptionChange = (index, value) => {
    setImagePreviews(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], caption: value };
      return updated;
    });
  };

  const saveIndividualPhoto = async (index) => {
    if (viewMode !== 'edit') {
      toast.error("You can only save individual photos in edit mode");
      return;
    }
    const photo = imagePreviews[index];
    if (photo.isExisting) return;

    // Find corresponding index in formData.images
    const nonExistingIndex = imagePreviews.slice(0, index).filter(p => !p.isExisting).length;
    const fileToUpload = formData.images[nonExistingIndex];
    if (!fileToUpload) return;

    setIsProcessingImages(true);
    try {
      const imgData = new FormData();
      imgData.append('file[]', fileToUpload);
      if (photo.caption) imgData.append('caption', photo.caption);

      const response = await addPOIImage(formData.id, imgData);
      if (response && (response.result || response.status === 200 || response.status === 201)) {
        toast.success("Image saved successfully");
        // Remove from formData.images so it's not uploaded again on main submit
        setFormData(prev => ({
          ...prev,
          images: prev.images.filter((_, i) => i !== nonExistingIndex)
        }));
        // Update imagePreviews to make it existing
        setImagePreviews(prev => {
          const updated = [...prev];
          updated[index] = { ...updated[index], isExisting: true, id: response.data?.data?.id || response.data?.id };
          return updated;
        });
      } else {
        toast.error("Failed to save image");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error saving image");
    } finally {
      setIsProcessingImages(false);
    }
  };

  const removePhoto = async (index) => {
    const photo = imagePreviews[index];

    if (viewMode === 'edit' && photo.isExisting) {
      setImageDeleteModal({ show: true, index });
    } else {
      // Remove locally selected image
      if (photo.url.startsWith('blob:')) {
        URL.revokeObjectURL(photo.url);
      }

      // Find corresponding index in formData.images
      const nonExistingIndex = imagePreviews.slice(0, index).filter(p => !p.isExisting).length;

      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== nonExistingIndex)
      }));
      setImagePreviews(prev => prev.filter((_, i) => i !== index));
    }
  };

  const confirmImageDelete = async () => {
    const index = imageDeleteModal.index;
    if (index === null) return;
    const photo = imagePreviews[index];

    setIsProcessingImages(true);
    setImageDeleteModal({ show: false, index: null });
    try {
      const res = await deletePOIImage(photo.id);
      if (res && res.result) {
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
        toast.success("Image deleted");
      }
    } catch (error) {
      console.error("Image deletion failed:", error);
    } finally {
      setIsProcessingImages(false);
    }
  };

  const handleMainImageChange = (e) => {
   const files = e.target.files ? Array.from(e.target.files) : [];
   if (files.length === 0) return;

   const file = files[0];
   if (file.size > 5 * 1024 * 1024) {
     return toast.error(`File is too large (>5MB)`);
   }

   setFormData(prev => ({
     ...prev,
     main_image: file
   }));

   const preview = {
     url: URL.createObjectURL(file),
     isExisting: false
   };
   setMainImagePreview(preview);
  };

  const removeMainImage = () => {
   // If it's an existing image in edit mode, show confirmation first
   if (viewMode === 'edit' && mainImagePreview?.isExisting && formData.id) {
     setMainImageDeleteModal({ show: true });
   } else {
     // Remove locally selected image
     if (mainImagePreview && mainImagePreview.url.startsWith('blob:')) {
       URL.revokeObjectURL(mainImagePreview.url);
     }
     setFormData(prev => ({
       ...prev,
       main_image: null
     }));
     setMainImagePreview(null);
   }
  };

  const confirmMainImageDelete = async () => {
   setMainImageDeleteModal({ show: false });
   setIsProcessingImages(true);
   try {
     const res = await deleteMainImage(formData.id);
     if (res && res.result) {
       if (mainImagePreview && mainImagePreview.url.startsWith('blob:')) {
         URL.revokeObjectURL(mainImagePreview.url);
       }
       setFormData(prev => ({
         ...prev,
         main_image: null
       }));
       setMainImagePreview(null);
       toast.success("Main image deleted successfully");
     } else {
       toast.error("Failed to delete main image");
     }
   } catch (error) {
     console.error("Main image deletion failed:", error);
     toast.error("Error deleting main image");
   } finally {
     setIsProcessingImages(false);
   }
  };

  const handleHourChange = (index, field, value) => {
    const updated = [...operatingHours];
    updated[index][field] = value;
    setOperatingHours(updated);
  };

  const toggleDayClosed = (index) => {
    const updated = [...operatingHours];
    updated[index].is_closed = !updated[index].is_closed;
    setOperatingHours(updated);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1 // Reset to first page on filter change
    }));
  };

  const handleAccessibilityChange = (item) => {
    if (accessibility.includes(item)) {
      setAccessibility(accessibility.filter(a => a !== item));
    } else {
      setAccessibility([...accessibility, item]);
    }
  };

  // Helper function to render stars with half-star support
  const renderStars = (rating, size = 20) => {
    const numRating = parseFloat(rating) || 0;
    return [1, 2, 3, 4, 5].map(star => {
      const fillPercent = Math.min(100, Math.max(0, (numRating - star + 1) * 100));
      return (
        <div key={star} style={{ position: 'relative', width: size, height: size }}>
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15 8.5 22 9.3 17 14 18.5 21 12 17.5 5.5 21 7 14 2 9.3 9 8.5 12 2" />
          </svg>
          {fillPercent > 0 && (
            <div style={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              width: `${fillPercent}%`, 
              height: '100%', 
              overflow: 'hidden' 
            }}>
              <svg width={size} height={size} viewBox="0 0 24 24" fill="var(--gold)" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15 8.5 22 9.3 17 14 18.5 21 12 17.5 5.5 21 7 14 2 9.3 9 8.5 12 2" />
              </svg>
            </div>
          )}
        </div>
      );
    });
  };

  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const h = parseInt(hours);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    const hDisplay = h12 < 10 ? `0${h12}` : h12;
    return `${hDisplay}:${minutes} ${suffix}`;
  };

  const parseTime = (timeStr) => {
    if (!timeStr) return '';
    // If it's already HH:mm, return it
    if (/^\d{2}:\d{2}$/.test(timeStr)) return timeStr;
    // Handle "06:00 AM" format
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':');
    let h = parseInt(hours);
    if (period === 'PM' && h < 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    const hStr = h < 10 ? `0${h}` : `${h}`;
    return `${hStr}:${minutes}`;
  };

  const getFloorLabel = (floor, floorDetails) => {
    if (typeof floor === 'string' && floor.trim()) return floor;
    if (floor && typeof floor === 'object') {
      const label = floor.name || floor.label || floor.floor_name;
      if (label) return label;
    }
    if (typeof floorDetails === 'string' && floorDetails.trim()) return floorDetails;
    if (floorDetails && typeof floorDetails === 'object') {
      const label = floorDetails.name || floorDetails.label || floorDetails.floor_name;
      if (label) return label;
    }
    return '-';
  };

  const handleEditClick = async (id) => {
    setIsLoading(true);
    try {
      const [res, dynamicFieldsRes] = await Promise.all([
        getSinglePOI(id),
        getPOIDynamicFields(id)
      ]);

      if (res && res.result) {
        const data = res.data;

        const airportCode = data.airport?.code;
        if (airportCode) {
          try {
            // 1. Fetch the records using the code
            const airportRes = await getAirportsSelection({ search: airportCode });

            if (airportRes?.result && airportRes.data.length > 0) {
              // 2. Find the specific airport object that matches the POI's airport_id
              const matchedAirport = airportRes.data.find(
                  (a) => String(a.id) === String(data.airport_id)
              );

              if (matchedAirport) {
                // 3. Update the search UI to match the expected display format
                setAirportSearch(`${matchedAirport.code} — ${matchedAirport.name}`);

                // 4. Update the available airports list so the selection is valid
                setAirports(prev => {
                  const exists = prev.find(a => a.id === matchedAirport.id);
                  return exists ? prev : [...prev, matchedAirport];
                });
              }
            }
          } catch (err) {
            console.error("Error fetching or matching airport:", err);
          }
        }


        const floorLabel = typeof data.floor === 'string' && data.floor.trim() ? data.floor : (data.floor?.name || data.floor?.label || data.floor?.floor_name || data.floor_details?.name || data.floor_details?.label || '');
        setFormData({
          id: data.id,
          name: data.name,
          airport_id: data.airport_id,
          concourse_id: data.concourse_id,
          category_id: data.category_id,
          floor_id: data.floor_id || data.floor?.id || data.floor?.floor_id || data.floor_details?.id || '',
          floor: floorLabel,
          lat: data.lat,
          lng: data.lng,
          description: data.description || '',
          phone_number: data.phone_number || '',
          website_url: data.website_url || '',
          internal_notes: data.internal_notes || '',
          status: data.status,
          tags: data.tags?.map(t => t.tag_name).join(', ') || '',
          images: [],
          main_image: null
        });

        // Load main image if it exists
        if (data.main_image) {
          setMainImagePreview({
            url: `${imgBaseUrl}${data.main_image}`,
            isExisting: true
          });
        } else {
          setMainImagePreview(null);
        }

        // Load POI dynamic fields
        if (dynamicFieldsRes && dynamicFieldsRes.result) {
          const fields = dynamicFieldsRes.data || [];
          console.log('POI Dynamic Fields Response:', fields);
          // Map the fields to ensure they have id property (API might return field_id)
          const mappedFields = fields.map(field => ({
            id: field.id || field.field_id,
            label: field.label,
            required: field.required || false
          }));
          console.log('Mapped POI Dynamic Fields:', mappedFields);
          setPoiDynamicFields(mappedFields);
        } else {
          console.log('No POI dynamic fields found');
          setPoiDynamicFields([]);
        }

        if (data.category) {
          setCategories(prev => {
            if (!prev.find(c => c.id === data.category_id)) {
              return [data.category, ...prev];
            }
            return prev;
          });
          setCatSearch(data.category.name);
          setIsCatSearching(false);
        }

         if (data.operating_hours && data.operating_hours.length > 0) {
           const hours = days.map(day => {
             const h = data.operating_hours.find(oh => oh.day === day);
             return h ? {
               day: h.day,
               open_time: h.is_closed ? '06:00' : parseTime(h.open_time),
               close_time: h.is_closed ? '22:00' : parseTime(h.close_time),
               is_closed: !!h.is_closed
             } : {
               day, open_time: '06:00', close_time: '22:00', is_closed: day === 'Sunday'
             };
           });
           setOperatingHours(hours);
         }

         if (data.accessibility) {
           setAccessibility(Array.isArray(data.accessibility) ? data.accessibility.map(a => a.feature_name || a) : []);
         }

         if (data.images && data.images.length > 0) {
           const previews = data.images.map(img => ({
             id: img.id,
             url: `${imgBaseUrl}${img.image_path}`,
             isExisting: true,
             caption: img.caption || ''
           }));
           setImagePreviews(previews);
         } else {
           setImagePreviews([]);
         }

         setViewMode('edit');
       } else {
         // toast.error(res?.message || "Failed to fetch POI details");
       }
     } catch (error) {
       console.log('An error occurred : ', error);
       // toast.error("Error fetching POI details");
     } finally {
       setIsLoading(false);
    }
  };

   const handleViewClick = async (id) => {
     setIsLoading(true);
     try {
       const [res, pendingRes, dynamicFieldsRes, reviewsRes] = await Promise.all([
         getSinglePOI(id),
         getPendingImages(id),
         getPOIDynamicFields(id),
         getPOIReviews(id)
       ]);
       if (res && res.result) {
          // Add dynamic fields to viewData if they exist separately
          let dynamicFieldsData = dynamicFieldsRes?.data || res.data?.dynamic_fields || [];

          console.log('Dynamic Fields Response:', dynamicFieldsRes);
          console.log('Dynamic Fields Data:', dynamicFieldsData);
          console.log('Available Dynamic Fields:', availableDynamicFields);

          // Map field IDs to get full field details from availableDynamicFields
          dynamicFieldsData = dynamicFieldsData.map(field => {
            const fullFieldData = availableDynamicFields.find(af => String(af.id) === String(field.id || field.field_id));
            console.log('Mapping field:', field, 'Found:', fullFieldData);
            return {
              id: field.id || field.field_id,
              label: fullFieldData?.label || field.label || field.field_label || `Field ${field.id || field.field_id}`,
              field_label: fullFieldData?.label,
              options: fullFieldData?.options || field.options || [],
              required: field.required || false
            };
          });

          console.log('Mapped Dynamic Fields:', dynamicFieldsData);

          const dataWithDynamicFields = {
            ...res.data,
            dynamic_fields: dynamicFieldsData
          };
          setViewData(dataWithDynamicFields);
         if (pendingRes && pendingRes.result) {
           setPendingImages(pendingRes.data);
        } else {
          setPendingImages([]);
        }
        
        // Process reviews and ratings from single API response
        if (reviewsRes && reviewsRes.result) {
          setPoiReviews(reviewsRes.data || []);
          setPoiRatings(reviewsRes.meta?.stats || null);
        } else {
          setPoiReviews([]);
          setPoiRatings(null);
        }
        
        setViewMode('view');
        } else {
          // toast.error(res?.message || "Failed to fetch POI details");
        }
      } catch (error) {
        console.log('An error occurred : ', error);
        // toast.error("Error fetching POI details");
      } finally {
        setIsLoading(false);
      }
    };

    const handleFetchReports = async (poiId) => {
      setLoadingReports(true);
      try {
        const res = await getPOIReports(poiId);
        if (res && res.result) {
          setPoiReports(res.data || []);
        } else {
          setPoiReports([]);
        }
      } catch (error) {
        console.error('Fetch reports error:', error);
        toast.error('Failed to load reports');
      } finally {
        setLoadingReports(false);
      }
    };

    const handleApproveReport = (reportId) => {
      setReportActionModal({ show: true, reportId, action: 'approve', reason: '' });
    };

    const handleRejectReport = (reportId) => {
      setReportActionModal({ show: true, reportId, action: 'reject', reason: '' });
    };

    const handleConfirmReportAction = async () => {
      const { reportId, action, reason } = reportActionModal;
      if (!reportId) return;

      try {
        const res = action === 'approve' 
          ? await approvePOIReport(reportId, reason)
          : await rejectPOIReport(reportId, reason);

        if (res && res.result) {
          toast.success(`Report ${action}d successfully`);
          setReportActionModal({ show: false, reportId: null, action: null, reason: '' });
          handleFetchReports(viewData.id);
        } else {
          toast.error(res?.message || `Failed to ${action} report`);
        }
      } catch (error) {
        console.error(`${action} report error:`, error);
        toast.error(`Error ${action}ing report`);
      }
    };

    const handleApproveImage = async (imageId) => {
    try {
      const res = await approveImage(imageId);
      if (res && res.result) {
        toast.success(res.message || "Image approved successfully");
        // refresh view data and pending images
        handleViewClick(viewData.id);
      } else {
        toast.error(res?.message || "Failed to approve image");
      }
    } catch (error) {
      console.log('Approve image error:', error);
      toast.error("Error approving image");
    }
  };

  const handleRejectImage = (imageId) => {
    // Show modal for rejection reason
    setRejectModal({ show: true, imageId, reason: '' });
  };

  const handleConfirmRejectImage = async () => {
    if (!rejectModal.imageId) return;

    setIsProcessingImages(true);
    try {
      const res = await rejectImage(rejectModal.imageId, rejectModal.reason);
      if (res && res.result) {
        toast.success(res.message || "Image rejected successfully");
        setRejectModal({ show: false, imageId: null, reason: '' });
        // refresh pending images
        handleViewClick(viewData.id);
      } else {
        toast.error(res?.message || "Failed to reject image");
      }
    } catch (error) {
      console.log('Reject image error:', error);
      toast.error("Error rejecting image");
    } finally {
      setIsProcessingImages(false);
    }
  };

  const toggleSelectPendingImage = (imageId) => {
    setSelectedPendingImages(prev =>
      prev.includes(imageId) ? prev.filter(id => id !== imageId) : [...prev, imageId]
    );
  };

  const toggleSelectAllPendingImages = () => {
    if (selectedPendingImages.length === pendingImages.length && pendingImages.length > 0) {
      setSelectedPendingImages([]);
    } else {
      setSelectedPendingImages(pendingImages.map(img => img.id));
    }
  };

  const handleBulkApprovePendingImages = async () => {
    if (selectedPendingImages.length === 0) return;

    setIsProcessingImages(true);
    try {
      await approveImage(selectedPendingImages);
      toast.success(`${selectedPendingImages.length} image(s) approved successfully`);
      setSelectedPendingImages([]);
      handleViewClick(viewData.id);
    } catch (error) {
      console.error('Bulk approve error:', error);
      toast.error("Error approving images");
    } finally {
      setIsProcessingImages(false);
    }
  };

  const handleBulkRejectPendingImages = () => {
    if (selectedPendingImages.length === 0) return;
    // Show modal for bulk rejection with reason
    setRejectModal({ show: true, imageId: 'bulk', reason: '', bulkIds: selectedPendingImages });
  };

  const handleConfirmBulkRejectPendingImages = async () => {
    if (!rejectModal.bulkIds || rejectModal.bulkIds.length === 0) return;

    setIsProcessingImages(true);
    try {
      // Call API once with all image IDs in array
      await rejectImage(rejectModal.bulkIds, rejectModal.reason);
      toast.success(`${rejectModal.bulkIds.length} image(s) rejected successfully`);
      setRejectModal({ show: false, imageId: null, reason: '', bulkIds: [] });
      setSelectedPendingImages([]);
      handleViewClick(viewData.id);
    } catch (error) {
      console.error('Bulk reject error:', error);
      toast.error("Error rejecting images");
    } finally {
      setIsProcessingImages(false);
    }
  };

   const confirmDelete = async () => {
    if (!deleteModal.id) return;
    setIsDeleting(true);
    try {
      const response = await deletePOI(deleteModal.id);
      if (response && (response.result || response.status === 200)) {
        toast.success(response.message || "POI deleted successfully");
        setDeleteModal({ show: false, id: null });
        setViewMode('list');
        // Refresh list
        const res = await getPOIs({ limit: 10 });
        if (res && res.result) setPoiList(res.data);
      } else {
        toast.error(response?.message || "Failed to delete POI");
      }
    } catch (error) {
      console.log('An error occurred : ', error);
      // toast.error("Error deleting POI");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    setIsLoading(true);
    try {
      const newStatus = currentStatus === 1 ? 2 : 1;
      const res = await updatePOIStatus({ ids: [id], status: String(newStatus) });
      if (res && (res.status === 200 || res.status === 201)) {
        toast.success(`POI ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`);
        handleViewClick(id);
      } else {
        toast.error('Failed to update POI status');
      }
    } catch (error) {
      console.error(error);
      toast.error('An error occurred while updating status');
    } finally {
      setIsLoading(false);
    }
  };

  const resetFormState = () => {
    setFormData({
      name: '',
      airport_id: '',
      concourse_id: '',
      category_id: '',
      floor_id: '',
      floor: '',
      lat: '',
      lng: '',
      description: '',
      tags: '',
      phone_number: '',
      website_url: '',
      internal_notes: '',
      status: 1,
     images: [],
     main_image: null
   });
   setPoiDynamicFields([]);
   setTagInput('');
   setTagsArray([]);
   setOperatingHours(
     days.map(day => ({
       day,
       open_time: '06:00',
       close_time: '22:00',
       is_closed: day === 'Sunday'
     }))
   );
   setAccessibility([]);
   setImagePreviews([]);
   setMainImagePreview(null);
   setCatSearch('');
   setIsCatSearching(false);
   setPoiReviews([]);
   setPoiRatings(null);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.name || !formData.airport_id || !formData.concourse_id || !formData.category_id || !formData.lat || !formData.lng) {
      return toast.error("Please fill in all required fields");
    }

    setIsSubmitting(true);
     try {
       const payload = {
         ...formData,
         lat: parseFloat(formData.lat) || 0,
         lng: parseFloat(formData.lng) || 0,
         operating_hours: operatingHours.map(h => ({
           ...h,
           open_time: h.is_closed ? null : h.open_time.includes('M') ? h.open_time : formatTime(h.open_time),
           close_time: h.is_closed ? null : h.close_time.includes('M') ? h.close_time : formatTime(h.close_time)
         })),
         accessibility: accessibility,
         dynamic_fields: poiDynamicFields.map((field, index) => ({
           field_id: field.id,
           order: index + 1,
           required: field.required || false
         }))
       };

       const finalPayload = new FormData();
       Object.entries(payload).forEach(([key, value]) => {
         if (key === 'id' && viewMode === 'edit') return;
         if (key === 'floor') return;
         if (key === 'main_image') return; // Handle main_image separately

         if (value !== null && value !== undefined) {
           if (key === 'operating_hours' && Array.isArray(value)) {
             finalPayload.append(key, JSON.stringify(value));
           } else if (key === 'accessibility' && Array.isArray(value)) {
             value.forEach(item => finalPayload.append('accessibility[]', item));
           } else if (key === 'dynamic_fields' && Array.isArray(value)) {
             finalPayload.append(key, JSON.stringify(value));
           } else if (key === 'images' && Array.isArray(value)) {
             // We handle images via addPOIImage API after POI creation/update
           } else {
             finalPayload.append(key, value);
           }
         }
       });

       // Add main_image if it exists
       if (formData.main_image instanceof File) {
         finalPayload.append('main_image', formData.main_image);
       }

       // Capture before resetFormState clears them
       const poiIdForImages = formData.id;
       const newImagesToUpload = (formData.images || []).filter(f => f instanceof File);

        const response = viewMode === 'edit' ? await updatePOI(formData.id, finalPayload) : await createPOI(finalPayload);
        if (response && (response.result || response.status === 201 || response.status === 200)) {
          const poiIdForImages = viewMode === 'edit' ? formData.id : (response.data?.data?.id || response.data?.id);

          // Upload any newly selected images via the separate image API
          if (newImagesToUpload.length > 0) {
           setIsProcessingImages(true);
           try {
             const nonExistingPreviews = imagePreviews.filter(p => !p.isExisting);
             for (let i = 0; i < newImagesToUpload.length; i++) {
               const file = newImagesToUpload[i];
               const imgData = new FormData();
               imgData.append('file[]', file);
               const caption = nonExistingPreviews[i]?.caption || '';
               if (caption) imgData.append('caption', caption);
               await addPOIImage(poiIdForImages, imgData);
             }
           } catch (imgError) {
             console.error('Image upload after update failed:', imgError);
             toast.error('POI updated but some images could not be uploaded');
           } finally {
             setIsProcessingImages(false);
           }
         }

         toast.success(response.message || `POI ${viewMode === 'edit' ? 'updated' : 'created'} successfully`);
         resetFormState();
         setViewMode('list');
         fetchPOIsData();
       } else {
        // toast.error(response?.message || `Failed to ${viewMode === 'edit' ? 'update' : 'create'} POI`);
      }
    } catch (error) {
      console.log('An error occurred : ', error);
      // toast.error(`An error occurred while ${viewMode === 'edit' ? 'updating' : 'saving'} POI`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle view parameter from URL (e.g., from notification click)
  useEffect(() => {
    const viewPoiId = searchParams.get('view');
    if (viewPoiId && viewMode === 'list') {
      handleViewClick(parseInt(viewPoiId));
    }
  }, [searchParams]);

  return (
    <>
      <div>
        <Topbar title="POI Management" subtitle="Manage all points of interest across all airports" />
        <div className="content">
          <div className="screens-stack">

            {/* 4.1 POI LIST */}
            {viewMode === 'list' && (
              <div>
                <div className="screen-label">4.1 — POI List</div>
                <div className="card">
                  <div className="toolbar">
                    <div className="search-wrap">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                      <input
                        className="search-input"
                        placeholder="Search POIs..."
                        value={filters.search}
                        name="search"
                        onChange={handleFilterChange}
                      />
                    </div>
                    <select className="filter-select" name="airport_id" value={filters.airport_id} onChange={handleFilterChange}>
                      <option value="">All Airports</option>
                      {airports.map(a => <option key={a.id} value={a.id}>{a.code}</option>)}
                    </select>
                    <select className="filter-select" name="category_id" value={filters.category_id} onChange={handleFilterChange}>
                      <option value="">All Categories</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <select className="filter-select" name="status" value={filters.status} onChange={handleFilterChange}>
                      <option value="">All Statuses</option>
                      <option value="1">Active</option>
                      <option value="2">Inactive</option>
                    </select>
                    <button className="btn btn-green">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
                      Import CSV
                    </button>
                    <button className="btn btn-primary" onClick={() => { resetFormState(); setViewMode('add'); }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                      Add POI
                    </button>
                  </div>
                  {selectedIds.length > 0 && (
                    <div className="bulk-bar" style={{ animation: 'slideUpFade 0.3s ease-out' }}>
                      <div className="bulk-info">{selectedIds.length} POIs selected</div>
                      <button
                        className="bulk-btn"
                        style={{ background: '#EAFAF3', color: '#1A7A42' }}
                        onClick={() => handleBulkStatusUpdate("1")}
                      >
                        Activate All
                      </button>
                      <button
                        className="bulk-btn"
                        style={{ background: '#FFF4E0', color: '#B8860B' }}
                        onClick={() => handleBulkStatusUpdate("2")}
                      >
                        Deactivate All
                      </button>
                    </div>
                  )}
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th><input type="checkbox" checked={poiList.length > 0 && selectedIds.length === poiList.length} onChange={toggleSelectAll} style={{ width: 14, height: 14, accentColor: 'var(--blue)', cursor: 'pointer' }} /></th>
                        <th>ID</th><th>POI Name</th><th>Airport</th><th>Concourse</th><th>Category</th><th>Status</th><th>Coordinates</th><th>Last Updated</th><th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {poiList.length > 0 ? poiList.map((p, i) => (
                        <tr key={i}>
                          <td><input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => toggleSelectItem(p.id)} style={{ width: 14, height: 14, accentColor: 'var(--blue)', cursor: 'pointer' }} /></td>
                          <td><span style={{ fontSize: 11, color: 'var(--grey)', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600 }}>#{p.id}</span></td>
                          <td><div style={{ fontWeight: 600, fontSize: 13, color: 'var(--navy)' }}>{p.name}</div><div style={{ fontSize: 11, color: 'var(--grey)' }}>{getFloorLabel(p.floor, p.floor_details) !== '-' ? getFloorLabel(p.floor, p.floor_details) : (p.level || '-')}</div></td>
                          <td><span className="tag">{p.airport_code || p.airport?.code}</span></td>
                          <td style={{ fontSize: 12, color: 'var(--grey)' }}>{p.concourse_details?.name || p.concourse?.name || '-'}</td>
                          <td><span style={{ background: '#EEF6FF', color: 'var(--blue)', fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 8 }}>{p.category_name || p.category?.name}</span></td>
                          <td><span className={`badge badge-${p.status === 1 ? 'active' : 'inactive'}`}>{p.status === 1 ? 'Active' : 'Inactive'}</span></td>
                          <td><span style={{ fontSize: 11, color: 'var(--grey)', fontFamily: "'Space Grotesk', sans-serif" }}>{p.lat}, {p.lng}</span></td>
                          <td style={{ fontSize: 12, color: 'var(--grey)' }}>{p.updatedAt ? new Date(p.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}</td>
                          <td>
                            <div className="action-btns">
                              {[
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />,
                                <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>,
                                <><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" /></>
                              ].map((icon, j) => (
                                <button key={j} className={`action-btn ${j === 2 ? 'danger' : ''}`} onClick={() => {
                                  if (j === 0) handleEditClick(p.id);
                                  if (j === 1) handleViewClick(p.id); // View is now index 1
                                  if (j === 2) setDeleteModal({ show: true, id: p.id }); // Delete is now index 2
                                }} title={j === 1 ? 'View Details' : ''}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8" strokeLinecap="round">{icon}</svg>
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="10" style={{ textAlign: 'center', padding: '40px', color: 'var(--grey)' }}>
                            {isLoading ? 'Loading POIs...' : 'No POIs found.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className="pagination">
                    <div className="page-info">Showing {(filters.page - 1) * filters.limit + 1}–{Math.min(filters.page * filters.limit, meta.total)} of {meta.total} POIs</div>
                    <div className="page-btns">
                      <button className="page-btn" disabled={filters.page === 1} onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}>‹</button>
                      {[...Array(meta.totalPages)].map((_, i) => (
                        <div
                          key={i}
                          className={`page-btn ${filters.page === i + 1 ? 'active' : ''}`}
                          onClick={() => setFilters(prev => ({ ...prev, page: i + 1 }))}
                        >
                          {i + 1}
                        </div>
                      ))}
                      <button className="page-btn" disabled={filters.page === meta.totalPages} onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}>›</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 4.2 ADD/EDIT POI + 4.3 ACTIONS */}
            {(viewMode === 'add' || viewMode === 'edit') && (
              <div>
                <div className="screen-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{viewMode === 'edit' ? '4.2 — Edit POI' : '4.2 — Add New POI'} &nbsp;·&nbsp; POI Actions</span>
                  <button className="btn btn-outline" onClick={() => setViewMode('list')} style={{ padding: '4px 12px', height: 32, fontSize: 12 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                    Back to List
                  </button>
                </div>
                <div className="card">
                  <div className="form-2col">
                    <div className="form-main">
                      <div className="fsec-title">Basic Information</div>
                      <div className="form-grid">
                        <div className="form-group full">
                          <label className="form-label">POI Name <span className="req">*</span></label>
                          <input className="form-input" name="name" value={formData.name} onChange={handleInputChange} placeholder='e.g. Starbucks, Gate B22, Restroom (Near A4)' required />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Airport <span className="req">*</span></label>
                          <div className="searchable-select" ref={airportDropdownRef}>
                            <div className="ss-input-wrap" onClick={() => setIsAirportDropdownOpen(!isAirportDropdownOpen)}>
                              <input
                                className="form-input"
                                placeholder="Search airport..."
                                value={airportSearch}
                                onChange={(e) => {
                                  setAirportSearch(e.target.value);
                                  setIsAirportSearching(true);
                                  setIsAirportDropdownOpen(true);
                                }}
                                onFocus={() => setIsAirportDropdownOpen(true)}
                                required
                                style={{ width: '100%' }}
                              />
                              <svg
                                className={`ss-arrow ${isAirportDropdownOpen ? 'open' : ''}`}
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="var(--grey)"
                                strokeWidth="2"
                              >
                                <path d="M6 9l6 6 6-6" />
                              </svg>
                            </div>
                            {isAirportDropdownOpen && (
                              <div className="ss-dropdown">
                                {airports.length > 0 ? (
                                  airports.map(a => (
                                    <div
                                      key={a.id}
                                      className={`ss-option ${formData.airport_id === a.id ? 'selected' : ''}`}
                                      onClick={() => {
                                        setFormData(prev => ({ ...prev, airport_id: a.id, concourse_id: '', floor_id: '' }));
                                        setAirportSearch(`${a.code} — ${a.name}`);
                                        setIsAirportSearching(false);
                                        setIsAirportDropdownOpen(false);
                                      }}
                                    >
                                      {a.code} — {a.name}
                                    </div>
                                  ))
                                ) : (
                                  <div className="ss-no-results">No airports found</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Concourse / Zone <span className="req">*</span></label>
                          <select className="form-select" name="concourse_id" value={formData.concourse_id} onChange={handleInputChange} required disabled={!formData.airport_id || loadingConcourses}>
                            <option value="">{loadingConcourses ? 'Loading concourses...' : 'Select Concourse'}</option>
                            {concourses.map(c => <option key={c.id} value={c.id}>{c.name || c.label}</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Floor / Level</label>
                          <select className="form-select" name="floor_id" value={formData.floor_id} onChange={handleInputChange} disabled={!formData.airport_id || loadingFloors}>
                            <option value="">{loadingFloors ? 'Loading floors...' : (floors.length === 0 ? 'No floors available (Optional)' : 'Select Floor (Optional)')}</option>
                            {floors.map(f => (
                              <option key={f.id} value={f.id}>{f.name || f.label || f.floor_name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group" style={{ position: 'relative' }} ref={catDropdownRef}>
                          <label className="form-label">Category <span className="req">*</span></label>
                          <div className="searchable-select">
                            <div className="ss-input-wrap" onClick={() => setIsCatDropdownOpen(!isCatDropdownOpen)}>
                              <input
                                className="form-input"
                                placeholder="Search category..."
                                value={catSearch}
                                onChange={(e) => {
                                  setCatSearch(e.target.value);
                                  setIsCatSearching(true);
                                  setIsCatDropdownOpen(true);
                                }}
                                onFocus={() => setIsCatDropdownOpen(true)}
                                required
                                style={{ width: '100%' }}
                              />
                              <svg
                                className={`ss-arrow ${isCatDropdownOpen ? 'open' : ''}`}
                                width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2"
                              >
                                <path d="M6 9l6 6 6-6" />
                              </svg>
                            </div>

                            {isCatDropdownOpen && (
                              <div className="ss-dropdown">
                                {categories.length > 0 ? (
                                  categories.map(c => (
                                    <div
                                      key={c.id}
                                      className={`ss-option ${formData.category_id === c.id ? 'selected' : ''}`}
                                      onClick={() => {
                                        setFormData(prev => ({ ...prev, category_id: c.id }));
                                        setCatSearch(c.name);
                                        setIsCatSearching(false);
                                        setIsCatDropdownOpen(false);
                                      }}
                                    >
                                      {c.name}
                                      {formData.category_id === c.id && (
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                          <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                      )}
                                    </div>
                                  ))
                                ) : (
                                  <div className="ss-no-results">No categories found</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="fsec-title" style={{ marginTop: 20 }}>GPS Coordinates</div>
                      <div className="form-grid">
                        <div className="form-group"><label className="form-label">Longitude <span className="req">*</span></label><input className="form-input" type="number" step="any" name="lng" value={formData.lng} onChange={handleInputChange} placeholder="e.g. -82.88442" required /></div>
                        <div className="form-group"><label className="form-label">Latitude <span className="req">*</span></label><input className="form-input" type="number" step="any" name="lat" value={formData.lat} onChange={handleInputChange} placeholder="e.g. 39.99768" required /></div>
                      </div>

                      <div className="fsec-title" style={{ marginTop: 20 }}>Description</div>
                      <div className="form-group">
                        <label className="form-label">User-Facing Description <span className="req">*</span></label>
                        <textarea className="form-textarea" name="description" value={formData.description} onChange={handleInputChange} placeholder="Shown in POI detail sheet. Max 300 characters." maxLength={300} required />
                        <div style={{ fontSize: 11, color: 'var(--grey)', textAlign: 'right', marginTop: 3 }}>{formData.description.length} / 300 characters</div>
                      </div>

                      <div className="fsec-title" style={{ marginTop: 20 }}>Operating Hours <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--grey)' }}> — Optional</span></div>
                      <div className="hours-grid">
                        {operatingHours.map((h, i) => (
                          <div key={i} className="hours-row" style={{ background: i % 2 === 0 ? 'var(--light)' : 'transparent' }}>
                            <div className="hours-day">{h.day}</div>
                            <input
                              className="hours-input"
                              type="time"
                              value={h.open_time}
                              onChange={(e) => handleHourChange(i, 'open_time', e.target.value)}
                              disabled={h.is_closed}
                            />
                            <input
                              className="hours-input"
                              type="time"
                              value={h.close_time}
                              onChange={(e) => handleHourChange(i, 'close_time', e.target.value)}
                              disabled={h.is_closed}
                            />
                            <div
                              onClick={() => toggleDayClosed(i)}
                              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: h.is_closed ? 'var(--red)' : 'var(--grey)', cursor: 'pointer' }}
                            >
                              <div style={{ width: 28, height: 16, borderRadius: 8, background: h.is_closed ? '#D1D5DB' : 'var(--green)', position: 'relative' }}>
                                <div style={{ position: 'absolute', width: 12, height: 12, borderRadius: '50%', background: 'white', top: 2, left: h.is_closed ? 2 : 14, transition: '0.2s' }} />
                              </div>
                              <span>{h.is_closed ? 'Closed' : 'Open'}</span>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="fsec-title" style={{ marginTop: 20 }}>Contact & Web <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--grey)' }}> — Optional</span></div>
                      <div className="form-grid">
                        <div className="form-group"><label className="form-label">Phone Number <span className="opt">Optional</span></label><input className="form-input" type="tel" name="phone_number" value={formData.phone_number} onChange={handleInputChange} placeholder="e.g. +1 (614) 555-0100" /></div>
                        <div className="form-group"><label className="form-label">Website URL <span className="opt">Optional</span></label><input className="form-input" type="url" name="website_url" value={formData.website_url} onChange={handleInputChange} placeholder="e.g. https://starbucks.com" /></div>
                      </div>

                      <div className="fsec-title" style={{ marginTop: 20 }}>Accessibility <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--grey)' }}> — Optional</span></div>
                      <div className="checkbox-group">
                        {['Wheelchair Accessible', 'Family Restroom', 'Braille Signage', 'Hearing Loop', 'Braille Menus Available'].map(a => (
                          <label key={a} className="check-item"><input type="checkbox" checked={accessibility.includes(a)} onChange={() => handleAccessibilityChange(a)} />{a}</label>
                        ))}
                      </div>

                      <div className="fsec-title" style={{ marginTop: 20 }}>Search Tags <span className="opt">Optional</span></div>
                      <div className="multi-select" style={{ minHeight: 40, padding: '4px 8px' }}>
                        {tagsArray.map((tag, i) => (
                          <div key={i} className="ms-pill" style={{ background: '#EEF6FF', color: 'var(--blue)', display: 'flex', alignItems: 'center', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>
                            {tag}
                            <span onClick={() => removeTag(tag)} style={{ cursor: 'pointer', marginLeft: 6, fontSize: 10 }}>✕</span>
                          </div>
                        ))}
                        <input
                          className="ms-input"
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={handleTagKeyDown}
                          placeholder={tagsArray.length === 0 ? 'Type tag and press Enter...' : ''}
                          style={{ border: 'none', flex: 1, outline: 'none', height: 28, fontSize: 13, background: 'transparent' }}
                        />
                      </div>

                      <div className="fsec-title" style={{ marginTop: 20 }}>Internal Notes <span className="opt">Optional — Admin only</span></div>
                      <textarea className="form-textarea" name="internal_notes" value={formData.internal_notes} onChange={handleInputChange} style={{ width: '100%' }} placeholder="e.g. Coordinates need on-site verification by Jimi." />
                    </div>

                    <div className="form-aside">
                      <div className="fsec-title">Status</div>
                      <div className="status-toggle-card">
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>POI Visibility</div>
                          <div style={{ fontSize: 11, color: 'var(--grey)', marginTop: 2 }}>Active = shown on map</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 12, color: 'var(--grey)' }}>Inactive</span>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, status: prev.status === 1 ? 2 : 1 }))}
                            className={`toggle-switch ${formData.status === 1 ? 'on' : 'off'}`}
                          />
                          <span style={{ fontSize: 12, fontWeight: 700, color: formData.status === 1 ? 'var(--green)' : 'var(--grey)' }}>Active</span>
                        </div>
                       </div>

                       <div className="fsec-title" style={{ marginTop: 24 }}>Associated Fields</div>
                       <div style={{ marginBottom: 20 }}>
                         <label className="form-label">Dynamic Fields</label>
                         <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
                           {availableDynamicFields.length > 0 ? (
                             <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {availableDynamicFields.map((field) => (
                                  <label key={field.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
                                    <input
                                      type="checkbox"
                                      checked={poiDynamicFields.some(df => String(df.id) === String(field.id))}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setPoiDynamicFields([...poiDynamicFields, { id: field.id, label: field.label }]);
                                        } else {
                                          setPoiDynamicFields(poiDynamicFields.filter(df => String(df.id) !== String(field.id)));
                                        }
                                      }}
                                      style={{ cursor: 'pointer' }}
                                    />
                                    <span>{field.label}</span>
                                    <span style={{ fontSize: 11, color: 'var(--grey)', marginLeft: 'auto' }}>
                                      {field.options?.length || 0} options
                                    </span>
                                  </label>
                                ))}
                             </div>
                           ) : (
                             <div style={{ color: 'var(--grey)', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
                               No dynamic fields available
                             </div>
                           )}
                         </div>
                          {poiDynamicFields.length > 0 && (
                            <div style={{ marginTop: 12, padding: 10, background: '#f0fdf4', border: '1px solid #dcfce7', borderRadius: 6 }}>
                              <div style={{ marginBottom: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {poiDynamicFields.map((df) => {
                                  // Get label from selected field, or find it from available fields
                                  const fieldLabel = df.label || availableDynamicFields.find(f => String(f.id) === String(df.id))?.label || `Field ${df.id}`;
                                  return (
                                    <span key={df.id} style={{ background: '#dcfce7', border: '1px solid #86efac', padding: '6px 10px', borderRadius: 6, fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, color: '#166534' }}>
                                      {fieldLabel}
                                      <button
                                        type="button"
                                        onClick={() => setPoiDynamicFields(poiDynamicFields.filter(item => String(item.id) !== String(df.id)))}
                                        style={{ background: 'none', border: 'none', color: '#166534', cursor: 'pointer', fontSize: 16, padding: 0, lineHeight: 1, width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', transition: 'background 0.2s' }}
                                        onMouseEnter={(e) => e.target.style.background = '#dcfce7'}
                                        onMouseLeave={(e) => e.target.style.background = 'none'}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                       </div>

                       <div className="fsec-title" style={{ marginTop: 16 }}>Main Image <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--grey)' }}>JPG/PNG · 5MB</span></div>
                       {mainImagePreview ? (
                        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                          <div style={{ position: 'relative', height: 180, width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img src={mainImagePreview.url} alt="Main Image" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            <button
                              type="button"
                              onClick={removeMainImage}
                              disabled={isProcessingImages}
                              style={{ position: 'absolute', top: 8, right: 8, background: 'var(--red)', color: 'white', border: 'none', borderRadius: '50%', width: 28, height: 28, fontSize: 14, cursor: isProcessingImages ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.2)', opacity: isProcessingImages ? 0.6 : 1 }}
                            >✕</button>
                          </div>
                        </div>
                       ) : (
                        <div className="photo-upload" onClick={() => document.getElementById('poi-main-image-upload').click()} style={{ cursor: 'pointer', marginBottom: 16 }}>
                          <input
                            type="file"
                            id="poi-main-image-upload"
                            hidden
                            accept="image/*"
                            onChange={handleMainImageChange}
                          />
                          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                          <div className="photo-upload-label">Upload Main Image</div>
                          <div className="photo-upload-sub">JPG or PNG · Max 5MB</div>
                        </div>
                       )}

                       <div className="fsec-title" style={{ marginTop: 16 }}>Photos <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--grey)' }}>Max 5 · JPG/PNG · 5MB</span></div>
                      <div className="photo-thumbs" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                        {imagePreviews.map((img, i) => (
                          <div key={i} className="photo-thumb-wrap" style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                            <div className="photo-thumb" style={{ position: 'relative', height: 110, width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <img src={img.url || img} alt={`POI ${i}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                              {!img.isExisting && viewMode === 'edit' && (
                                <button
                                  type="button"
                                  onClick={() => saveIndividualPhoto(i)}
                                  style={{ position: 'absolute', top: 6, right: 32, background: '#10b981', color: 'white', border: 'none', borderRadius: '50%', width: 22, height: 22, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
                                  title="Save Photo"
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => removePhoto(i)}
                                style={{ position: 'absolute', top: 6, right: 6, background: 'var(--red)', color: 'white', border: 'none', borderRadius: '50%', width: 22, height: 22, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
                              >✕</button>
                            </div>
                            <input
                              type="text"
                              className="form-input"
                              placeholder="Caption (optional)"
                              style={{ fontSize: 11, padding: '6px 10px', height: 28, width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                              value={img.caption || ''}
                              onChange={(e) => handleCaptionChange(i, e.target.value)}
                              disabled={img.isExisting}
                            />
                          </div>
                        ))}
                      </div>
                      {imagePreviews.length < 5 && (
                        <div className="photo-upload" onClick={() => document.getElementById('poi-photos-upload').click()} style={{ cursor: 'pointer' }}>
                          <input
                            type="file"
                            id="poi-photos-upload"
                            multiple
                            hidden
                            accept="image/*"
                            onChange={handleImageChange}
                          />
                          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                          <div className="photo-upload-label">Upload Photos</div>
                          <div className="photo-upload-sub">JPG or PNG · Max 5MB · Up to 5 photos</div>
                        </div>
                      )}


                    </div>
                  </div>
                  <div className="form-footer">
                    <button className="btn btn-outline" onClick={() => { resetFormState(); setViewMode('list'); }}>Cancel</button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /></svg>
                          {viewMode === 'edit' ? 'Update POI' : 'Save POI'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {viewMode === 'view' && viewData && (
              <div>
                <div className="screen-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>4.4 — POI Details &nbsp;·&nbsp; {viewData.name}</span>
                  <button className="btn btn-outline" onClick={() => setViewMode('list')} style={{ padding: '4px 12px', height: 32, fontSize: 12 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                    Back to List
                  </button>
                </div>
                <div className="card">
                  <div className="form-2col">
                    <div className="form-main">
                      <div className="fsec-title">Basic Information</div>
                      <div className="detail-grid">
                        <div className="detail-item"><label>Name</label><div>{viewData.name}</div></div>
                        <div className="detail-item"><label>Airport</label><div>{viewData.airport?.code} — {viewData.airport?.name}</div></div>
                        <div className="detail-item"><label>Concourse</label><div>{viewData.concourse_details?.name || '-'}</div></div>
                        <div className="detail-item"><label>Floor</label><div>{getFloorLabel(viewData.floor, viewData.floor_details)}</div></div>
                        <div className="detail-item"><label>Category</label><div>{viewData.category?.name}</div></div>
                        <div className="detail-item"><label>Status</label><div><span className={`badge badge-${viewData.status === 1 ? 'active' : 'inactive'}`}>{viewData.status === 1 ? 'Active' : 'Inactive'}</span></div></div>
                      </div>

                      <div className="fsec-title" style={{ marginTop: 24 }}>Location & Contact</div>
                      <div className="detail-grid">
                        <div className="detail-item"><label>Coordinates</label><div>{viewData.lat}, {viewData.lng}</div></div>
                        <div className="detail-item"><label>Website</label><div>{viewData.website_url || '-'}</div></div>
                        <div className="detail-item"><label>Phone</label><div>{viewData.phone_number || '-'}</div></div>
                      </div>

                      <div className="fsec-title" style={{ marginTop: 24 }}>Description</div>
                      <div className="detail-content" style={{ fontSize: 13, background: '#f8fafc', padding: 15, borderRadius: 8, color: '#475569', lineHeight: 1.6 }}>
                        {viewData.description || 'No description provided.'}
                      </div>

                      <div className="fsec-title" style={{ marginTop: 24 }}>Operating Hours</div>
                      <div className="detail-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                        {viewData.operating_hours?.map(h => (
                          <div key={h.day} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                            <span style={{ fontWeight: 600, fontSize: 12 }}>{h.day}</span>
                            <span style={{ fontSize: 12, color: h.is_closed ? 'var(--red)' : 'var(--grey)' }}>
                              {h.is_closed ? 'Closed' : `${h.open_time} - ${h.close_time}`}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="fsec-title" style={{ marginTop: 24 }}>Features & Tags</div>
                      <div className="detail-item"><label style={{ marginBottom: 8 }}>Accessibility</label>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {viewData.accessibility?.length > 0 ? viewData.accessibility.map((a, i) => (
                            <span key={i} style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: 6, fontSize: 11 }}>{a.feature_name || a}</span>
                          )) : '-'}
                        </div>
                      </div>
                      <div className="detail-item" style={{ marginTop: 16 }}><label style={{ marginBottom: 8 }}>Tags</label>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {viewData.tags?.length > 0 ? viewData.tags.map((t, i) => (
                            <span key={i} style={{ background: '#EEF6FF', color: 'var(--blue)', padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{t.tag_name || t}</span>
                          )) : '-'}
                        </div>
                       </div>

                        <div className="fsec-title" style={{ marginTop: 24 }}>Associated Dynamic Fields</div>
                        <div className="detail-item"><label style={{ marginBottom: 8 }}>Dynamic Fields</label>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {viewData.dynamic_fields && viewData.dynamic_fields.length > 0 ? (
                              viewData.dynamic_fields.map((field, i) => {
                                // Find full field info from availableDynamicFields if label is missing
                                const fullFieldInfo = !field.label ? availableDynamicFields.find(af => String(af.id) === String(field.id)) : null;
                                const fieldLabel = field.label || fullFieldInfo?.label || field.field_label || `Field ${field.id}`;
                                const fieldOptions = field.options || fullFieldInfo?.options || [];

                                return (
                                  <div key={i} style={{ background: '#f0fdf4', border: '1px solid #dcfce7', padding: '8px 12px', borderRadius: 6, fontSize: 12 }}>
                                    <div style={{ fontWeight: 600, color: '#166534', marginBottom: 4 }}>{fieldLabel}</div>
                                    <div style={{ fontSize: 11, color: '#4b7a5e' }}>
                                      {fieldOptions.length || 0} option(s) {field.required && <span style={{ marginLeft: 4, color: '#dc2626' }}>• Required</span>}
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <span style={{ color: 'var(--grey)', fontSize: 12 }}>No dynamic fields attached</span>
                            )}
                          </div>
                        </div>

                        {/* Reviews & Ratings Section */}
                        <div className="fsec-title" style={{ marginTop: 32 }}>Reviews & Ratings</div>
                        <div style={{ marginTop: 12 }}>
                          {poiRatings && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {renderStars(poiRatings.average_rating, 20)}
                              </div>
                              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)' }}>
                                {poiRatings.average_rating ? parseFloat(poiRatings.average_rating).toFixed(1) : '0.0'}
                              </div>
                              <div style={{ fontSize: 13, color: 'var(--grey)' }}>
                                ({poiRatings.total_ratings || 0} review{poiRatings.total_ratings !== 1 ? 's' : ''})
                              </div>
                            </div>
                          )}
                          
                          {poiReviews && poiReviews.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                              {poiReviews.map((review, i) => (
                                <div key={i} style={{ 
                                  padding: 16, 
                                  background: '#f8fafc', 
                                  borderRadius: 10, 
                                  border: '1px solid #e2e8f0'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
                                    <div style={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: '50%',
                                      background: 'linear-gradient(135deg, #66B2FF, #0A1F44)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'white',
                                      fontWeight: 700,
                                      fontSize: 14,
                                      flexShrink: 0
                                    }}>
                                      {(review.User?.full_name || review.user?.full_name || 'U').charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--navy)' }}>
                                          {review.User?.full_name || review.user?.full_name || 'Unknown User'}
                                        </div>
                                        <div style={{ display: 'flex', gap: 1 }}>
                                          {renderStars(review.rating, 14)}
                                        </div>
                                      </div>
                                      <div style={{ fontSize: 11, color: 'var(--grey)' }}>
                                        {review.User?.email || review.user?.email || ''}
                                      </div>
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--grey)' }}>
                                      {review.created_at ? new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                                    </div>
                                  </div>
                                  {review.comment && (
                                    <div style={{ 
                                      fontSize: 13, 
                                      color: 'var(--navy)', 
                                      lineHeight: 1.5,
                                      paddingLeft: 52
                                    }}>
                                      {review.comment}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ fontSize: 13, color: 'var(--grey)' }}>No reviews found.</div>
                          )}
                        </div>

                       <div className="fsec-title" style={{ marginTop: 32 }}>Main Image</div>
                       {viewData.main_image ? (
                         <div style={{ marginTop: 12 }}>
                           <div style={{ width: 300, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff', display: 'flex', flexDirection: 'column' }}>
                             <div style={{ width: '100%', height: 200, flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setSelectedImage(`${imgBaseUrl}${viewData.main_image}`)}>
                               <img src={`${imgBaseUrl}${viewData.main_image}`} alt="Main Image" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                             </div>
                           </div>
                         </div>
                       ) : (
                         <div style={{ fontSize: 13, color: 'var(--grey)', marginTop: 12 }}>No main image set.</div>
                       )}

                       <div className="fsec-title" style={{ marginTop: 32 }}>Approved Images</div>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 }}>
                        {viewData.images && viewData.images.length > 0 ? (
                          viewData.images.map((img, i) => (
                            <div key={i} style={{ width: 180, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff', display: 'flex', flexDirection: 'column' }}>
                              <div style={{ width: '100%', height: 120, flexShrink: 0, cursor: 'pointer' }} onClick={() => setSelectedImage(`${imgBaseUrl}${img.image_path}`)}>
                                <img src={`${imgBaseUrl}${img.image_path}`} alt={`Approved ${i}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                              </div>
                              <div style={{ padding: 10, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                <div style={{ fontSize: 11, color: 'var(--grey)', marginBottom: 4 }}>Uploaded by:</div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{img.User?.full_name || 'Unknown User'}</div>
                                <div style={{ fontSize: 11, color: 'var(--grey)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minHeight: 16 }}>{img.User?.email || '\u00A0'}</div>
                                {img.caption && <div style={{ fontSize: 12, marginBottom: 8, fontStyle: 'italic', color: 'var(--navy)' }}>"{img.caption}"</div>}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div style={{ fontSize: 13, color: 'var(--grey)' }}>No approved images found.</div>
                        )}
                      </div>

                      <div className="fsec-title" style={{ marginTop: 32 }}>Pending Images</div>
                      {selectedPendingImages.length > 0 && (
                        <div className="bulk-bar" style={{ animation: 'slideUpFade 0.3s ease-out', marginBottom: 16 }}>
                          <div className="bulk-info">{selectedPendingImages.length} image(s) selected</div>
                          <button
                            className="bulk-btn"
                            style={{ background: '#EAFAF3', color: '#1A7A42' }}
                            onClick={handleBulkApprovePendingImages}
                            disabled={isProcessingImages}
                          >
                            Approve All
                          </button>
                          <button
                            className="bulk-btn"
                            style={{ background: '#FFF0F0', color: '#FF5C5C' }}
                            onClick={handleBulkRejectPendingImages}
                            disabled={isProcessingImages}
                          >
                            Reject All
                          </button>
                        </div>
                       )}

                      {/* Group pending images by user */}
                      {pendingImages && pendingImages.length > 0 ? (
                        Object.entries(
                          pendingImages.reduce((groups, img) => {
                            const userId = img.User?.id || 'unknown';
                            const userName = img.User?.full_name || 'Unknown User';
                            const userEmail = img.User?.email || '';
                            if (!groups[userId]) {
                              groups[userId] = {
                                userId,
                                userName,
                                userEmail,
                                images: []
                              };
                            }
                            groups[userId].images.push(img);
                            return groups;
                          }, {})
                        ).map(([userId, userGroup]) => (
                          <div key={userId} style={{ marginBottom: 32 }}>
                            {/* User Header */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              marginBottom: 16,
                              paddingBottom: 12,
                              borderBottom: '2px solid #E8EDF3'
                            }}>
                              <div style={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #66B2FF, #0A1F44)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 700,
                                fontSize: 14,
                                flexShrink: 0
                              }}>
                                {userGroup.userName.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)' }}>
                                  {userGroup.userName}
                                </div>
                                <div style={{ fontSize: 12, color: 'var(--grey)' }}>
                                  {userGroup.userEmail}
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                              {userGroup.images.map((img, i) => (
                                <div key={i} style={{ width: 180, border: `2px solid ${selectedPendingImages.includes(img.id) ? 'var(--blue)' : '#e2e8f0'}`, borderRadius: 8, overflow: 'hidden', background: selectedPendingImages.includes(img.id) ? '#F8FBFF' : '#fff', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                                  <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 10 }}>
                                    <input
                                      type="checkbox"
                                      checked={selectedPendingImages.includes(img.id)}
                                      onChange={() => toggleSelectPendingImage(img.id)}
                                      style={{ width: 18, height: 18, accentColor: 'var(--blue)', cursor: 'pointer' }}
                                    />
                                  </div>
                                  <div style={{ width: '100%', height: 120, flexShrink: 0, cursor: 'pointer' }} onClick={() => setSelectedImage(`${imgBaseUrl}${img.image_path}`)}>
                                    <img src={`${imgBaseUrl}${img.image_path}`} alt={`Pending ${i}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                  </div>
                                  <div style={{ padding: 10, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                    {img.caption && <div style={{ fontSize: 12, marginBottom: 8, fontStyle: 'italic', color: 'var(--navy)' }}>"{img.caption}"</div>}
                                    <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
                                      <button className="btn btn-primary" style={{ flex: 1, padding: '4px 0', fontSize: 11, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => handleApproveImage(img.id)}>Approve</button>
                                      <button className="btn btn-danger" style={{ flex: 1, padding: '4px 0', fontSize: 11, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => handleRejectImage(img.id)}>Reject</button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={{ fontSize: 13, color: 'var(--grey)' }}>No pending images found.</div>
                      )}
                    </div>

                    <div className="form-aside">
                      <div className="fsec-title">Internal Info</div>
                      <div className="aside-card ac-grey">POI ID: #{viewData.id}</div>
                      <div className="aside-card ac-blue">Added By: {viewData.creator?.full_name || `User #${viewData.created_by}`}</div>
                      <div className="aside-card ac-grey">Date Created: {new Date(viewData.createdAt).toLocaleDateString()}</div>
                      <div className="aside-card ac-grey">Last Update: {new Date(viewData.updatedAt).toLocaleDateString()}</div>

                      <div className="fsec-title" style={{ marginTop: 24 }}>Admin Notes</div>
                      <div className="detail-content" style={{ fontSize: 12, background: '#fffbeb', border: '1px solid #fef3c7', padding: 12, borderRadius: 8, color: '#92400e' }}>
                        {viewData.internal_notes || 'No internal notes.'}
                      </div>

                      <div className="fsec-title" style={{ marginTop: 24 }}>POI Actions</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {/* Edit */}
                        <button
                          className="btn btn-primary"
                          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                          onClick={() => handleEditClick(viewData.id)}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                          Edit POI
                        </button>

                        {/* Toggle Active/Inactive */}
                        <button
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                            background: viewData.status === 1 ? '#FFF4E0' : '#EAFAF3',
                            color: viewData.status === 1 ? '#B8860B' : '#1A7A42',
                            border: `1px solid ${viewData.status === 1 ? '#F9E0A0' : '#A0E0C0'}`,
                            borderRadius: 8,
                            padding: '9px 16px',
                            fontWeight: 600,
                            fontSize: 13,
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onClick={() => handleToggleStatus(viewData.id, viewData.status)}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18.36 6.64A9 9 0 1 1 5.64 17.36M12 2v10" />
                          </svg>
                          {viewData.status === 1 ? 'Deactivate POI' : 'Activate POI'}
                        </button>

                                                  {/* Report POI */}
                          <button
                            className="btn btn-outline"
                            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                            onClick={() => {
                              handleFetchReports(viewData.id);
                              setViewMode('reports');
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M12 9v2m0 4v.01M5.07 19A7.02 7.02 0 11 12 4.06a7 7 0 016.93 14.94A7 7 0 015.07 19z" />
                            </svg>
                            Report POI
                          </button>

                          {/* Delete */}
                        <button
                          className="btn btn-danger"
                          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                          onClick={() => setDeleteModal({ show: true, id: viewData.id })}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" />
                          </svg>
                          Delete POI
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* 4.5 POI Reports */}
              {viewMode === 'reports' && viewData && (
                <div>
                  <div className="screen-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>4.5 — POI Reports &nbsp;·&nbsp; {viewData.name}</span>
                    <button className="btn btn-outline" onClick={() => setViewMode('view')} style={{ padding: '4px 12px', height: 32, fontSize: 12 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}>
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                      </svg>
                      Back to Details
                    </button>
                  </div>
                  <div className="card">
                    <div style={{ padding: 20 }}>
                      <div className="fsec-title" style={{ marginBottom: 16 }}>POI Reports</div>
                      {loadingReports ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--grey)' }}>Loading reports...</div>
                      ) : poiReports.length > 0 ? (
                        <table className="tbl">
                          <thead>
                            <tr>
                              <th style={{ width: 50 }}>#</th>
                              <th>User</th>
                              <th>Email</th>
                              <th>Reason</th>
                              <th>Status</th>
                              <th>Admin Reason</th>
                              <th>Date</th>
                              <th style={{ width: 120 }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {poiReports.map((report) => (
                              <tr key={report.id}>
                                <td><span style={{ fontSize: 11, color: 'var(--grey)', fontWeight: 600 }}>#{report.id}</span></td>
                                <td><span style={{ fontWeight: 600, fontSize: 13, color: 'var(--navy)' }}>{report.user?.full_name || 'Unknown'}</span></td>
                                <td><span style={{ fontSize: 12, color: 'var(--grey)' }}>{report.user?.email || '-'}</span></td>
                                <td><span style={{ fontSize: 13, color: 'var(--navy)' }}>{report.reason || '-'}</span></td>
                                <td><span className={`badge badge-${report.status === 'approved' || report.status === 1 ? 'active' : report.status === 'rejected' || report.status === 2 ? 'inactive' : 'pending'}`}>{report.status === 'approved' || report.status === 1 ? 'Approved' : report.status === 'rejected' || report.status === 2 ? 'Rejected' : 'Pending'}</span></td>
                                <td><span style={{ fontSize: 12, color: 'var(--grey)' }}>{report.admin_reason || '-'}</span></td>
                                <td><span style={{ fontSize: 12, color: 'var(--grey)' }}>{report.createdAt ? new Date(report.createdAt).toLocaleDateString() : '-'}</span></td>
                                <td>
                                  <div className="action-btns">
                                    <button className="action-btn" title="Approve" onClick={() => handleApproveReport(report.id)} style={{ width: 28, height: 28, background: (report.status === 'approved' || report.status === 1) ? '#d1fae5' : undefined }}>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                                        <polyline points="20 6 9 17 4 12" />
                                      </svg>
                                    </button>
                                    <button className="action-btn danger" title="Reject" onClick={() => handleRejectReport(report.id)} style={{ width: 28, height: 28, background: (report.status === 'rejected' || report.status === 2) ? '#fee2e2' : undefined }}>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--grey)' }}>No reports found for this POI.</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
  
            </div>
          </div>
  
          {/* Report Action Modal */}
        {reportActionModal.show && (
          <div className="modal-overlay" onClick={() => setReportActionModal({ show: false, reportId: null, action: null, reason: '' })}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ width: 480 }}>
              <div className="modal-icon" style={{ background: reportActionModal.action === 'approve' ? '#D1FAE5' : '#FEE2E2', color: reportActionModal.action === 'approve' ? '#10B981' : '#EF4444', marginBottom: 20 }}>
                {reportActionModal.action === 'approve' ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                )}
              </div>
              <h3 style={{ fontSize: 18, color: 'var(--navy)', marginBottom: 10 }}>
                {reportActionModal.action === 'approve' ? 'Approve' : 'Reject'} Report
              </h3>
              <p style={{ fontSize: 14, color: 'var(--grey)', lineHeight: 1.5, marginBottom: 20 }}>
                Are you sure you want to {reportActionModal.action} this report?
              </p>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 8, textAlign: 'left' }}>
                  Reason (Optional)
                </label>
                <textarea
                  value={reportActionModal.reason}
                  onChange={(e) => setReportActionModal(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Enter reason..."
                  style={{
                    width: '100%',
                    minHeight: 80,
                    padding: 10,
                    borderRadius: 6,
                    border: '1px solid #E2E8F0',
                    fontSize: 13,
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div className="modal-actions">
                <button className="btn btn-outline" onClick={() => setReportActionModal({ show: false, reportId: null, action: null, reason: '' })}>Cancel</button>
                <button
                  className={reportActionModal.action === 'approve' ? 'btn btn-green' : 'btn btn-danger'}
                  onClick={handleConfirmReportAction}
                >
                  Confirm {reportActionModal.action === 'approve' ? 'Approval' : 'Rejection'}
                </button>
              </div>
            </div>
          </div>
        )}

          {/* Delete Confirmation Modal */}
        {deleteModal.show && (
          <div className="modal-overlay">
            <div className="modal-card">
              <div className="modal-icon danger">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" /></svg>
              </div>
              <h3>Delete Point of Interest?</h3>
              <p>Are you sure you want to remove this POI? This action cannot be undone and it will be permanently removed from the airport map.</p>
              <div className="modal-actions">
                <button className="btn btn-outline" onClick={() => setDeleteModal({ show: false, id: null })} disabled={isDeleting}>Cancel</button>
                <button className="btn btn-danger" onClick={confirmDelete} disabled={isDeleting}>
                  {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Full Image Modal */}
        {selectedImage && (
          <div className="modal-overlay" onClick={() => setSelectedImage(null)} style={{ zIndex: 9999, cursor: 'pointer' }}>
            <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <img src={selectedImage} alt="Enlarged view" style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: '8px', backgroundColor: 'transparent' }} />
              <button
                style={{ position: 'absolute', top: '-15px', right: '-15px', background: 'var(--red)', color: 'white', border: 'none', borderRadius: '50%', width: '30px', height: '30px', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.2)' }}
                onClick={(e) => { e.stopPropagation(); setSelectedImage(null); }}
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Rejection Reason Modal */}
        {rejectModal.show && (
          <div className="modal-overlay" onClick={() => !isProcessingImages && setRejectModal({ show: false, imageId: null, reason: '', bulkIds: [] })}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ width: 480 }}>
              <div className="modal-icon" style={{ background: '#FEE2E2', color: '#DC2626', marginBottom: 20 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              </div>
              <h3 style={{ fontSize: 18, color: 'var(--navy)', marginBottom: 10 }}>
                Reject Image{rejectModal.bulkIds && rejectModal.bulkIds.length > 1 ? 's' : ''}?
              </h3>
              <p style={{ fontSize: 14, color: 'var(--grey)', lineHeight: 1.5, marginBottom: 20 }}>
                Are you sure you want to reject this {rejectModal.bulkIds && rejectModal.bulkIds.length > 1 ? 'image' : 'image'}?
              </p>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 8, textAlign: 'left' }}>
                  Rejection Reason (Optional)
                </label>
                <textarea
                  value={rejectModal.reason}
                  onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
                  placeholder="Enter reason for rejection (user will be notified)..."
                  style={{
                    width: '100%',
                    minHeight: 80,
                    padding: 10,
                    borderRadius: 6,
                    border: '1px solid #E2E8F0',
                    fontSize: 13,
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                  disabled={isProcessingImages}
                />
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-outline"
                  onClick={() => !isProcessingImages && setRejectModal({ show: false, imageId: null, reason: '', bulkIds: [] })}
                  disabled={isProcessingImages}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    if (rejectModal.bulkIds && rejectModal.bulkIds.length > 0) {
                      handleConfirmBulkRejectPendingImages();
                    } else {
                      handleConfirmRejectImage();
                    }
                  }}
                  disabled={isProcessingImages}
                >
                  {isProcessingImages ? 'Rejecting...' : 'Yes, Reject'}
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
        .detail-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; }
        .detail-item label { display: block; font-size: 11px; font-weight: 700; color: var(--grey); text-transform: uppercase; margin-bottom: 4px; }
        .detail-item div { font-size: 14px; color: var(--navy); font-weight: 500; }

        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal-card { background: white; width: 400px; padding: 30px; border-radius: 12px; text-align: center; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); }
        .modal-icon { width: 50px; height: 50px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 15px; }
        .modal-icon.danger { background: #fee2e2; color: #dc2626; }
        .modal-card h3 { font-size: 18px; color: var(--navy); margin-bottom: 10px; }
        .modal-card p { font-size: 14px; color: var(--grey); line-height: 1.5; margin-bottom: 25px; }
        .modal-actions { display: flex; gap: 12px; justify-content: center; }
        .btn-danger { background: #dc2626; color: white; border: none; padding: 8px 20px; border-radius: 6px; font-weight: 600; cursor: pointer; }
        .btn-danger:hover { background: #b91c1c; }
        .btn-danger:disabled { opacity: 0.5; cursor: not-allowed; }

        /* Searchable Select Styles */
        .searchable-select { width: 100%; position: relative; }
        .ss-input-wrap { position: relative; width: 100%; }
        .ss-arrow { 
          position: absolute; right: 14px; top: 50%; transform: translateY(-50%); 
          color: var(--grey); pointer-events: none; transition: 0.3s; 
        }
        .ss-arrow.open { transform: translateY(-50%) rotate(180deg); color: var(--blue); }
        .ss-dropdown { 
          position: absolute; top: calc(100% + 6px); left: 0; right: 0; 
          background: white; border: 1px solid #E2E8F0; border-radius: 12px; 
          box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1); 
          z-index: 1000; max-height: 280px; overflow-y: auto; 
          animation: slideUpFade 0.2s ease-out;
        }
        .ss-option { 
          padding: 12px 16px; font-size: 13px; color: var(--navy); 
          cursor: pointer; transition: all 0.2s; 
          display: flex; align-items: center; justify-content: space-between;
        }
        .ss-option:hover { background: #F8FAFC; color: var(--blue); padding-left: 20px; }
        .ss-option.selected { background: #F0F7FF; color: var(--blue); font-weight: 700; }
        .ss-no-results { padding: 20px; font-size: 13px; color: var(--grey); text-align: center; }

        @keyframes slideUpFade {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Custom Scrollbar for dropdown */
        .ss-dropdown::-webkit-scrollbar { width: 6px; }
        .ss-dropdown::-webkit-scrollbar-track { background: transparent; }
        .ss-dropdown::-webkit-scrollbar-thumb { background: #E2E8F0; border-radius: 10px; }
        .ss-dropdown::-webkit-scrollbar-thumb:hover { background: #CBD5E1; }
      `}</style>
        {imageDeleteModal.show && (
          <div className="modal-overlay">
            <div className="modal-card">
              <div className="modal-icon danger">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 6L6 18M6 6l12 12" /></svg>
              </div>
              <h3>Remove Photo?</h3>
              <p>Are you sure you want to permanently delete this photo? This action is immediate and cannot be reversed.</p>
              <div className="modal-actions">
                <button className="btn btn-outline" onClick={() => setImageDeleteModal({ show: false, index: null })}>Cancel</button>
                <button className="btn btn-danger" onClick={confirmImageDelete}>Delete Photo</button>
              </div>
            </div>
          </div>
        )}

        {mainImageDeleteModal.show && (
          <div className="modal-overlay">
            <div className="modal-card">
              <div className="modal-icon danger">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 6L6 18M6 6l12 12" /></svg>
              </div>
              <h3>Remove Main Image?</h3>
              <p>Are you sure you want to permanently delete this main image? This action is immediate and cannot be reversed.</p>
              <div className="modal-actions">
                <button className="btn btn-outline" onClick={() => setMainImageDeleteModal({ show: false })}>Cancel</button>
                <button className="btn btn-danger" onClick={confirmMainImageDelete}>Delete Main Image</button>
              </div>
            </div>
          </div>
        )}


      </div>
      {(isLoading || isSubmitting || isDeleting || isProcessingImages || loadingReviews || loadingReports) && <Spinner />}
    </>
  );
};

export default POIManagement;





